import React, { createContext, useState, useEffect, useContext } from "react";
import { useAuth } from "@clerk/clerk-react";
import { SERVER_URL } from '../constants';
import { toast } from "react-toastify";

// Create a context for the cart
const CartContext = createContext();

// CartContextProvider to wrap your components
export const CartContextProvider = ({ children }) => {
  const [cartId, setCartId] = useState(null);
  const [cart, setCart] = useState([]);
  const [amount, setAmount] = useState(0);
  const { isSignedIn, getToken } = useAuth();

  useEffect(() => {
    const getCart = async () => {
      if (!isSignedIn) return;
  
      try {
        const token = await getToken();
        const response = await fetch(`${SERVER_URL}/api/v1/cart`, {
          method: "GET",
          headers: { "Content-Type": "application/json" },
          credentials: "include",
        });
  
        if (!response.ok) throw new Error(`HTTP error! Status: ${response.status}`);
  
        const result = await response.json();
        if (result.statusCode === 404) return;
  
        setCartId(result.data?._id);
        setCart(result.data?.items || []);
        setAmount(result.data?.amount || 0);
      } catch (error) {
        console.error("Error fetching cart:", error);
      }
    };
  
    getCart();
  }, [isSignedIn]);
  

  const addToCart = async (item) => {
    try {
      if (!isSignedIn) {
        return; // No error displayed to users
      }
      const token = await getToken();
      const response = await fetch(`${SERVER_URL}/api/v1/cart/add`, {
        method: "POST",
        credentials: "include",
        headers: {
          "Content-Type": "application/json",
        },
        body: JSON.stringify(item),
      });
  
      const result = await response.json();
      if (!result.success) {
        console.error("Add to cart failed:", result.message);
        return;
      }
  
      toast.success(result.message);
      setCartId(result.data._id);
      setCart(result.data.items);
      setAmount(result.data.amount);
    } catch (error) {
      console.error("Error adding to cart:", error);
      // No toast error shown to users
    }
  };
  

  const updateQuantity = async (item, increment) => {
    try {
      if (!isSignedIn) return;
  
      const product = { productId: item.productId, variationId: item.variationId, increment };
      const token = await getToken();
      const response = await fetch(`${SERVER_URL}/api/v1/cart/update`, {
        method: "POST",
        credentials: "include",
        headers: { "Content-Type": "application/json" },
        body: JSON.stringify(product),
      });
  
      const result = await response.json();
      if (!result.success) {
        console.error("Update quantity failed:", result.message);
        return;
      }
  
      toast.success(result.message);
      setCart(result.data.items);
      setAmount(result.data.amount);
    } catch (error) {
      console.error("Error updating cart quantity:", error);
    }
  };
  

  const removeFromCart = async (item) => {
    // item should be the product object with productId & variationId if exist
    try {
      if (!isSignedIn) {
        toast.error("To Remove Items From Cart, Please Sign In.");
        return;
      }
      const token = await getToken();
      const product = {productId: item.productId, variationId: item?.variationId};
      const response = await fetch(`${SERVER_URL}/api/v1/cart/remove`, {
        method: "DELETE",
        credentials: "include",
        headers: {
   
          "Content-Type": "application/json",
        },
        body: JSON.stringify(product),
      });
      const result = await response.json();
      if (!result.success) {
        throw new Error(result.message);
      }
      toast.success(result.message);
      setCart(result.data.items);
      setAmount(result.data.amount);
    } catch (error) {
      console.log(error);
      toast.error("Something Is Wrong. Please Try Again Later.");
    }
  };

  return (
    <CartContext.Provider
      value={{ cart, cartId, amount, addToCart, updateQuantity, removeFromCart }}
    >
      {children}
    </CartContext.Provider>
  );
};

// Custom hook to use the Cart context
export const useCart = () => {
  const context = useContext(CartContext);
  if (!context) {
    throw new Error("useCart must be used within a CartContextProvider");
  }
  return context;
};
